import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google"
import AppleSignin, {
  appleAuthHelpers,
  AppleAuthResponse,
  useScript
} from "react-apple-signin-auth"
import {
  Center,
  CircularProgress,
  Flex,
  FlexProps,
  Stack
} from "@chakra-ui/react"
import { AppleIcon } from "./appleIcon"
import styled from "@emotion/styled"
import React, { ReactNode, useState } from "react"
import { SDKContext } from "../../../context/SDKContext"
import { GoogleIcon } from "./googleIcon"
import { AuthPageStyling } from "src/context/types"
import axios from "src/shared/axios"
import { useHistory } from "react-router-dom"
import useConfig from "src/hooks/useConfig"
import { Routes } from "src/constants"
import {
  useIsAndroidApp,
  useIsIOSApp,
  useIsMobileApp
} from "src/util/deviceType"
import { MdOutlineEmail } from "react-icons/md"
import useSearchParams from "src/hooks/useSearchParams"
import { createHost } from "src/conditionals/Authentication"

export const StyledButton = styled.button<
  AuthPageStyling & { darkmode?: boolean }
>`
  font-size: 14px;
  font-weight: 500;
  font-family: "Google Sans", arial, sans-serif;
  color: ${props =>
    props?.darkmode
      ? "white"
      : props?.authPageButtonTextColor
      ? props?.authPageButtonTextColor
      : "#3c4043"};
  border: 1px solid #dadce0;
  border-color: ${props =>
    props?.authPageButtonBorderColor
      ? props?.authPageButtonBorderColor
      : "#dadce0"};
  background-color: ${props =>
    props?.darkmode
      ? "rgba(0, 0, 0, 0.65)"
      : props?.authPageButtonBackgroundColor
      ? props?.authPageButtonBackgroundColor
      : "transparent"};
  height: 40px;
  border-radius: ${props =>
    props?.darkmode
      ? "20px"
      : props?.authPageButtonBorderRadius
      ? props?.authPageButtonBorderRadius + "px"
      : "4px"};
  letter-spacing: 0.25px;
  transition: background-color 0.218s, border-color 0.218s;
  &:hover {
    color: ${props =>
      props?.darkmode
        ? "white"
        : props?.authPageButtonMouseoverTextColor
        ? props?.authPageButtonMouseoverTextColor
        : "#3c4043"};
    border-color: ${props =>
      props?.authPageButtonBorderMouseoverColor
        ? props.authPageButtonBorderMouseoverColor
        : "#d2e3fc"};
    background-color: ${props =>
      props?.darkmode
        ? "transparent"
        : props?.authPageButtonMouseoverBackgroundColor
        ? props?.authPageButtonMouseoverBackgroundColor
        : "rgba(66, 133, 244, 0.04)"};
  }
`

const ConditionalWrap = ({
  shouldWrap,
  children,
  ...rest
}: {
  shouldWrap: boolean
  children: ReactNode
} & FlexProps) => {
  if (shouldWrap) {
    return (
      <Flex width="100%" {...rest}>
        {children}
      </Flex>
    )
  }
  return <>{children}</>
}

const ConditionalCenter = ({
  shouldWrap,
  children
}: {
  shouldWrap: boolean
  children: ReactNode
}) => {
  if (shouldWrap) {
    return <Center>{children}</Center>
  }
  return <>{children}</>
}

export const OAuthLogin = (props: {
  googleOAuthId: string
  appleOAuthId: string
  authPageStyling: AuthPageStyling
  darkmode?: boolean
  displayAsRow?: boolean
  oAuthRedirectURI: string
  showEmailAndPasswordButton: boolean
}) => {
  const [scriptLoaded, setScriptLoaded] = useState(false)
  return (
    <GoogleOAuthProvider
      clientId={props.googleOAuthId}
      onScriptLoadSuccess={() => setScriptLoaded(true)}
    >
      <_OAuthLogin {...props} googleScriptLoaded={scriptLoaded} />
    </GoogleOAuthProvider>
  )
}

export const _OAuthLogin = ({
  appleOAuthId,
  authPageStyling,
  darkmode,
  displayAsRow = false,
  oAuthRedirectURI,
  showEmailAndPasswordButton,
  googleScriptLoaded
}: {
  googleOAuthId: string
  appleOAuthId: string
  authPageStyling: AuthPageStyling
  darkmode?: boolean
  displayAsRow?: boolean
  oAuthRedirectURI: string
  showEmailAndPasswordButton: boolean
  googleScriptLoaded: boolean
}) => {
  const { postLoginRedirectUrl, iosAppUrlScheme, googleOauthId, platformName } =
    useConfig()
  const history = useHistory()
  const meetingClient = React.useContext(SDKContext)
  // we remap subdomain.domain.tld/AUTH_PAGE => domain.tld/AUTH_PAGE?partner=subdomain
  // so here we check if that has happened and ensure that the host contains the subdomain if so
  const queryParams = new URLSearchParams(window.location.search)
  const partnerParam = queryParams.get("partner")
  const partnerSubdomain = partnerParam != null ? partnerParam + "." : ""
  const [appleLoading, setAppleLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const isAndroidApp = useIsAndroidApp()
  const isMobileApp = useIsMobileApp()
  let { isSecureInAppBrowser, googleOAuthOnLoad, code, state } =
    useSearchParams<{
      isSecureInAppBrowser?: true | undefined
      googleOAuthOnLoad?: true | undefined
      code?: string | undefined
      state?: string | undefined
    }>()
  const decodedState = state != null ? JSON.parse(state) : null
  if (decodedState) {
    isSecureInAppBrowser = decodedState.isSecureInAppBrowser
  }
  const isIOSApp = useIsIOSApp()
  const isWKWebView = isIOSApp && !isSecureInAppBrowser
  const isRegisterPage = window.location.pathname === Routes.REGISTER

  useScript(appleAuthHelpers.APPLE_SCRIPT_SRC)

  enum RedirectType {
    inApp,
    browser
  }

  const [redirectState, setRedirectState] = useState<
    | {
        type: RedirectType.browser
      }
    | {
        type: RedirectType.inApp
        code: string
      }
    | null
  >(null) // hack/fix: at page load time the postLoginRedirectUrl config may not be ready yet, temporarily returning the value 'loading'. so we use a combination of a useEffect + state to await it.

  const appleLogin = () => {
    appleAuthHelpers.signIn({
      authOptions: {
        clientId: appleOAuthId,
        redirectURI: oAuthRedirectURI,
        scope: "email",
        usePopup: true
      },
      onSuccess: handleAppleSuccess,
      onError: (
        err: any /* the library does not provide a type for this error */
      ) => console.log(err)
    })
  }

  const redirectUri =
    "https://" +
    createHost(meetingClient!.envConfig.DOMAIN_HOSTNAME) +
    window.location.pathname

  console.log("redirectUri", redirectUri)
  const googleLoginExecutor = useGoogleLogin({
    // onSuccess: ({ code }) => handleGoogleSuccess(code),
    ux_mode: "redirect",
    redirect_uri: redirectUri,
    state: JSON.stringify({ isSecureInAppBrowser }),
    flow: "auth-code"
  })

  React.useEffect(() => {
    if (googleOAuthOnLoad && googleScriptLoaded) {
      googleLoginExecutor()
    }
  }, [googleOAuthOnLoad, googleLoginExecutor, googleScriptLoaded])

  const googleLogin = () => {
    if (isWKWebView) {
      // inside of wkwebview we want to open a new window and perform the google flow in there (on load)
      let url = new URL(window.location.href)
      let params = new URLSearchParams(url.search)
      params.set("isSecureInAppBrowser", "true")
      params.set("googleOAuthOnLoad", "true")
      url.search = params.toString()
      const domain = createHost(meetingClient!.envConfig.DOMAIN_HOSTNAME)
      // there isn't an easy way (as far as im aware) of intercepting the url created by useGoogleLogin
      // this is done to avoid loading the utta login page inside of the safari popup, instead opening the google login url directly
      const pageURL = `https://accounts.google.com/o/oauth2/v2/auth/oauthchooseaccount?gsiwebsdk=3&client_id=${googleOauthId}&scope=openid%20profile%20email&redirect_uri=https%3A%2F%2F${domain}%2Fuser-login&prompt=consent&state=%7B%22isSecureInAppBrowser%22%3A%22true%22%7D&access_type=offline&response_type=code&include_granted_scopes=true&enable_granular_consent=true&service=lso&o2v=2&ddm=0&flowName=GeneralOAuthFlow`
      meetingClient?.nativeBridge?.eventOpenPageInSecureBrowser({
        pageURL
      })
    } else {
      googleLoginExecutor()
    }
  }

  const handleGoogleSuccess = async (code: string) => {
    if (!meetingClient) throw "Meeting client not ready"
    setGoogleLoading(true)
    const res = await axios.post(
      meetingClient?.envConfig.OAUTH_URL,
      {
        provider: "google",
        code,
        host: partnerSubdomain + meetingClient?.envConfig.DOMAIN_HOSTNAME,
        redirectUri: redirectUri
      },
      {
        withCredentials: true
      }
    )
    setGoogleLoading(false)
    if (res.status == 200) {
      if (isSecureInAppBrowser) {
        const res = await axios.get(meetingClient?.envConfig.ONE_TIME_CODE, {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json"
          }
        })
        const code = res.data.code
        // this navigation will get intercepted by deep linking, telling the native app to close the secure safari browser and load this url in WKWebView instead to consume the one-time-code
        // document.location = `https://utta.app/one-time-code?code=${code}&host=${createHost(meetingClient?.envConfig.DOMAIN_HOSTNAME)}&redirectTo=${obtainRedirectURL()}`
        // we have to use a custom url scheme to get the link to trigger inside of embedded safari
        setRedirectState({ type: RedirectType.inApp, code })
      } else {
        setRedirectState({ type: RedirectType.browser })
      }
    }
  }

  React.useEffect(() => {
    if (redirectState && postLoginRedirectUrl !== "loading") {
      switch (redirectState.type) {
        case RedirectType.browser:
          history.replace(postLoginRedirectUrl)
          break
        case RedirectType.inApp:
          if (!meetingClient) throw "Meeting client not ready"
          document.location = `${iosAppUrlScheme}://one-time-code?code=${
            redirectState.code
          }&host=${createHost(
            meetingClient?.envConfig.DOMAIN_HOSTNAME
          )}&redirectTo=${postLoginRedirectUrl}`
          break
      }
    }
  }, [redirectState])

  React.useEffect(() => {
    if (code != null) {
      handleGoogleSuccess(code)
    }
  }, [code])

  const handleAppleSuccess = async (appleRes: AppleAuthResponse) => {
    if (!meetingClient) throw "Meeting client not ready"
    setAppleLoading(true)
    const res = await axios.post(
      meetingClient?.envConfig.OAUTH_URL,
      {
        provider: "apple",
        code: appleRes.authorization.code,
        host: partnerSubdomain + meetingClient?.envConfig.DOMAIN_HOSTNAME
      },
      {
        withCredentials: true
      }
    )
    setAppleLoading(false)
    if (res.status == 200) {
      history.replace(postLoginRedirectUrl)
    }
  }

  const emailLogin = () => {
    // currently we only show this button in the secure in app browser
    // so we only need to worry about dispatching the event that the native app is expecting
    let url = new URL(window.location.href)
    let params = new URLSearchParams(url.search)
    params.set("isSecureInAppBrowser", "true")
    url.search = params.toString()
    meetingClient?.nativeBridge?.eventOpenPageInSecureBrowser({
      pageURL: url.toString()
    })
  }

  return (
    <>
      {!isSecureInAppBrowser && (
        <ConditionalCenter shouldWrap={!displayAsRow}>
          <Flex direction={displayAsRow ? "row" : "column"} gap="1.5">
            <ConditionalWrap
              shouldWrap={displayAsRow}
              justifyContent="flex-end"
            >
              {/* Hide the google login inside of webviews for now as it doesn't work in embedded browsers */}
              <StyledButton
                {...authPageStyling}
                onClick={googleLogin}
                darkmode={darkmode}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                    padding: "0 12px"
                  }}
                >
                  {googleLoading ? (
                    <Stack width={"100%"} align={"center"}>
                      <CircularProgress
                        isIndeterminate
                        value={30}
                        color={
                          authPageStyling?.authPageButtonTextColor ??
                          "orange.400"
                        }
                        thickness="12px"
                        size={"20px"}
                      />
                    </Stack>
                  ) : (
                    <>
                      <div
                        style={{
                          marginRight: "8px",
                          ...(darkmode ? { filter: "brightness(100)" } : {})
                        }}
                      >
                        <GoogleIcon />
                      </div>
                      {"Continue with Google"}
                    </>
                  )}
                </div>
              </StyledButton>
            </ConditionalWrap>
            <ConditionalWrap shouldWrap={displayAsRow}>
              {/* Hide the Apple login from Android but not from iOS or desktop */}
              <StyledButton
                {...authPageStyling}
                onClick={appleLogin}
                darkmode={darkmode}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                    padding: "0 12px"
                  }}
                >
                  {appleLoading ? (
                    <Stack width={"100%"} align={"center"}>
                      <CircularProgress
                        isIndeterminate
                        value={30}
                        color={
                          authPageStyling?.authPageButtonTextColor ??
                          "orange.400"
                        }
                        thickness="12px"
                        size={"20px"}
                      />
                    </Stack>
                  ) : (
                    <>
                      <div
                        style={{
                          marginRight: "8px",
                          ...(darkmode
                            ? { filter: "invert(100%) brightness(1000)" }
                            : {})
                        }}
                      >
                        <AppleIcon />
                      </div>
                      {"Continue with Apple"}
                    </>
                  )}
                </div>
              </StyledButton>
            </ConditionalWrap>

            {showEmailAndPasswordButton && (
              <StyledButton
                {...authPageStyling}
                onClick={emailLogin}
                darkmode={darkmode}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                    padding: "0 12px"
                  }}
                >
                  {false ? (
                    <Stack width={"100%"} align={"center"}>
                      <CircularProgress
                        isIndeterminate
                        value={30}
                        color={
                          authPageStyling?.authPageButtonTextColor ??
                          "orange.400"
                        }
                        thickness="12px"
                        size={"20px"}
                      />
                    </Stack>
                  ) : (
                    <>
                      <div
                        style={{
                          marginRight: "8px",
                          ...(darkmode && platformName !== "LearnMachine"
                            ? { filter: "invert(100%) brightness(1000)" }
                            : {})
                        }}
                      >
                        <MdOutlineEmail
                          color={
                            platformName === "LearnMachine" ? "white" : "black"
                          }
                          size={20}
                        />
                      </div>
                      {"Continue with Email"}
                    </>
                  )}
                </div>
              </StyledButton>
            )}
          </Flex>
        </ConditionalCenter>
      )}
    </>
  )
}
